import merge from 'lodash.merge';
import React, { useCallback, useState } from 'react';
import { Accept, DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import styled, { css } from 'styled-components';
import { formatBytes, printArray } from '../../../common/utils';
import { useFocusCss } from '../../../fundamentals/actions';
import { PX_TO_REM, SIZES } from '../../../fundamentals/sizes/sizes';
import { IGutterProps, useGutterAsMarginCss, useGutterCss } from '../../../fundamentals/spaces/space';
import { useTestId } from '../../../hooks/useTestId/useTestId';
import { ErrorText } from '../../ErrorText/ErrorText';
import { FeaturedIcon } from '../../FeaturedIcon/FeaturedIcon';
import { HelpText } from '../../HelpText/HelpText';
import { MATERIAL_ICONS } from '../../Icon/icons';
import { LinkButton } from '../../LinkButton/LinkButton';
import { P } from '../../Texts/Texts';
import { fileTypeExtensionMap, FileUploadAllowedFiles } from '../fileTypes';
import { IBorderRadiusProps, useBorderRadiusCss } from '../../../fundamentals/sizes/borderRadius';
import { BaseStateKey } from '../../../fundamentals/colours/colourInterfaces';
import { VARIANTS } from '../../../fundamentals/colours/variants';
import { BaseDiv } from '../../../common/BaseElements';

export interface IFilePickerProps extends Pick<DropzoneOptions, 'maxSize' | 'disabled'> {
  multiple?: boolean;
  acceptedFileTypes?: FileUploadAllowedFiles[];
  translations?: IFilePickerTranslations;
  errors?: string[];
  onFilesSelected?: (files: File[]) => void;
}

export interface IFilePickerTranslations {
  cta?: string;
  instruction?: string;
  failed?: string;
  or?: string;
  max?: string;
  helpText?: string;
}

const useFilePickerCss = css`
  cursor: pointer;
  ${({ theme }) => css`
    border: solid 1px ${theme.fileUploadColours.default.border};
    background-color: ${theme.fileUploadColours.default.background};
    &:hover {
      background-color: ${theme.fileUploadColours.hover.background};
    }
    &:active {
      background-color: ${theme.fileUploadColours.active.background};
    }
    &:focus {
      ${useFocusCss()}
    }
  `}
`;

const useDisabledCss = css`
  background-color: ${({ theme }) => theme.fileUploadColours.disabled.background};
`;

const DragAndDropContainer = styled(BaseDiv)<IFilePickerProps & { error: boolean } & IBorderRadiusProps & IGutterProps>`
  ${useBorderRadiusCss()};

  width: 100%;
  padding: ${PX_TO_REM[24]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${useGutterAsMarginCss({ bottom: 16 })}
  ${({ disabled }) => (disabled ? useDisabledCss : useFilePickerCss)}  
  ${({ theme, error }) =>
    error &&
    css`
      border-color: ${theme.fileUploadColours.error.border};
    `}
`;

const Text = styled(P)<IFilePickerProps & IGutterProps>`
  ${useGutterCss({ top: 4 })};
  ${({ disabled, theme }) =>
    disabled &&
    css`
      color: ${theme.fileUploadColours.disabled.text};
    `};
`;

export const acceptedFilesRequest = (acceptedFileTypes?: FileUploadAllowedFiles[]): Accept => {
  const accept: Accept = {};
  acceptedFileTypes?.forEach((aft) => (accept[aft] = fileTypeExtensionMap[aft]));
  return accept;
};

export const acceptedFileTypesMessage = (maxSize: number, translations: { max?: string; or?: string }, acceptedFileTypes?: FileUploadAllowedFiles[]) =>
  [
    printArray(
      acceptedFileTypes?.map((aft) => fileTypeExtensionMap[aft]?.join('/').replaceAll('.', '').toUpperCase()),
      translations.or
    ),
    `(${translations.max} ${formatBytes(maxSize, 0)})`
  ]
    .join(' ')
    .trim();

export const FilePicker: React.FC<IFilePickerProps> = ({ maxSize = 5_000_000, multiple, acceptedFileTypes, onFilesSelected, ...rest }) => {
  const { generateTestId } = useTestId(rest, 'FilePicker');
  const [errors, setErrors] = useState<string[]>(rest.errors ?? []);
  const [iconState, setIconState] = useState<BaseStateKey | undefined>();

  const defaultTranslations: IFilePickerTranslations = { max: 'Max.', failed: '' };
  const translations = merge(defaultTranslations, rest.translations);

  const handleOnDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
    acceptedFiles?.length > 0 && onFilesSelected?.(acceptedFiles);
    setErrors(
      fileRejections.map(({ file, errors: rejectionErrors }) =>
        [translations.failed, `(${file.name})`, rejectionErrors?.map(({ message }) => `${message}`).join('. ')].join(' ')
      )
    );
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleOnDrop,
    maxSize,
    multiple,
    disabled: rest.disabled,
    accept: acceptedFilesRequest(acceptedFileTypes)
  });

  const acceptedFileTypesString = acceptedFileTypesMessage(maxSize, translations, acceptedFileTypes);

  const updateIconState = (cssState?: BaseStateKey) => setIconState(cssState);

  return (
    <>
      {translations.helpText && <HelpText {...generateTestId('HelpText')}>{translations.helpText}</HelpText>}
      {errors?.map((error, key) => (
        <ErrorText key={key} {...generateTestId(`Error.${key}`)}>
          {error}
        </ErrorText>
      ))}
      <DragAndDropContainer
        {...rest}
        {...generateTestId('DragAndDrop')}
        {...getRootProps()}
        error={errors?.length! > 0}
        onMouseLeave={() => updateIconState()}
        onMouseEnter={() => updateIconState(`hover`)}
        onMouseDown={() => updateIconState(`active`)}
        onMouseUp={() => updateIconState()}
        data-iconstate={iconState}
        borderRadius={SIZES.SMALL}
      >
        <input {...getInputProps()} />
        <FeaturedIcon
          disabled={rest.disabled}
          variant={VARIANTS.SECONDARY}
          light
          size={SIZES.LARGE}
          name={MATERIAL_ICONS.CloudUpload}
          gutterBottom={16}
          cssState={iconState}
        />
        {translations.cta && (
          <LinkButton {...generateTestId('CtaLink')} disabled={rest.disabled}>
            {translations.cta}
          </LinkButton>
        )}

        {translations.instruction && (
          <Text {...generateTestId('Instruction')} {...rest}>
            {translations.instruction}
          </Text>
        )}
        {acceptedFileTypesString && (
          <Text {...generateTestId('AcceptedFiles')} {...rest}>
            {acceptedFileTypesString}
          </Text>
        )}
      </DragAndDropContainer>
    </>
  );
};
